import React, { useCallback, useEffect, useRef, useState } from "react";
import { APIProvider, Map, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { LocateFixed } from "lucide-react";
import { useTheme } from "@/shared/context/ThemeContext";
import LocationPuck from "@/shared/maps/LocationPuck";
import useUserPuck from "@/shared/maps/useUserPuck";

/**
 * Branded interactive Google Maps view for the LINK booking flow.
 *
 * Behavior
 *  - Pinch-to-zoom + one-finger pan via gestureHandling="greedy".
 *  - Custom Swiss-Editorial map style (cool greys + cream water).
 *  - Dual-layer route polyline (orange halo under bright orange line) with an
 *    animated draw-on (≈700ms) and round endpoint caps via two circular pins.
 *  - Pickup pin = #0074d4, destination pin = #ed6500.
 *  - User-location pulsing blue dot rendered as a marker + animated Circle ring.
 *  - Floating "Recenter on me" button bottom-right (above the page CTA bar).
 *  - Stale polylines are torn down before any redraw — only one route exists.
 *  - On Maps JS auth failure, transparently falls back to the static map image.
 *
 * Props
 *  - center        { lat, lng }   fallback / empty-state center
 *  - polyline      string         Google encoded polyline (overview)
 *  - userLocation  { lat, lng } | null  resolved geolocation, drives the dot
 *  - fallbackSrc   string         static-map URL used when the JS API fails
 */
const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

/** Swiss-Editorial map style — cool greys for built environment, soft green
 *  for parks/natural land, cream water. Roads stay clean & light so the
 *  orange→blue gradient route polyline pops. Restoring landscape.natural +
 *  poi.park brings depth back to the map without re-introducing icon clutter. */
const MAP_STYLES_LIGHT = [
  // Default geometry (covers buildings/built-up land) — soft warm grey.
  { elementType: "geometry", stylers: [{ color: "#eef1f6" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#525f7f" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
  // Natural land (forests, fields, undeveloped) — soft sage green.
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#dde9d4" }] },
  { featureType: "landscape.natural.terrain", elementType: "geometry", stylers: [{ color: "#d4e2c8" }] },
  // Parks — slightly more saturated green so they read clearly.
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#c8dcb6" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#5a7a47" }] },
  // Built-up land / man-made areas — keep neutral.
  { featureType: "landscape.man_made", elementType: "geometry", stylers: [{ color: "#f1f3f7" }] },
  // Roads.
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#e4e7ec" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#d0d5dd" }] },
  // Water — soft blue cream.
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#bcd9f5" }] },
  // Hide POI icons (still show parks geometry) + transit.
  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
  { featureType: "poi.medical", stylers: [{ visibility: "off" }] },
  { featureType: "poi.school", stylers: [{ visibility: "off" }] },
  { featureType: "poi.place_of_worship", stylers: [{ visibility: "off" }] },
  { featureType: "poi.sports_complex", stylers: [{ visibility: "off" }] },
  { featureType: "poi.attraction", stylers: [{ visibility: "off" }] },
  { featureType: "poi.government", stylers: [{ visibility: "off" }] },
  { featureType: "poi", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
];

/** Dark companion — same shape, tuned to match the app's deep-navy chrome.
 *  Parks/natural rendered as muted forest green so the route still pops. */
const MAP_STYLES_DARK = [
  { elementType: "geometry", stylers: [{ color: "#0f172a" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#9ca3af" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0f172a" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#1f2937" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#1a2a1f" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#22392a" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#9bbf8a" }] },
  { featureType: "landscape.man_made", elementType: "geometry", stylers: [{ color: "#111827" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#1f2937" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#9ca3af" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#374151" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#d1d5db" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0b1220" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#4b5563" }] },
  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
  { featureType: "poi.medical", stylers: [{ visibility: "off" }] },
  { featureType: "poi.school", stylers: [{ visibility: "off" }] },
  { featureType: "poi.place_of_worship", stylers: [{ visibility: "off" }] },
  { featureType: "poi.sports_complex", stylers: [{ visibility: "off" }] },
  { featureType: "poi.attraction", stylers: [{ visibility: "off" }] },
  { featureType: "poi.government", stylers: [{ visibility: "off" }] },
  { featureType: "poi", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
];

const ROUTE_ORANGE = "#ed6500";
const PICKUP_BLUE = "#0074d4";
const USER_BLUE = "#0074d4";

export default function RouteMap({
  center,
  polyline,
  userLocation,
  fallbackSrc,
  hideRecenter = false,
  userHeading = null,
  userMarkerVariant = "default",   // "default" (rider blue) | "driver" (branded orange + heading arrow)
  userMarkerState = "en_route",    // driver-only: "idle" | "en_route" | "on_trip"
  onMapClick = null,               // fires on tap of the map background (used to collapse overlays)
  cinematicDraw = false,           // when true, animate the polyline progressively (~750ms) on first appear
  onDrawComplete = null,           // fires once the cinematic polyline draw finishes (chain hand-off to caller)
  recenterAnchor = "bottom",       // "bottom" (default) | "top" — where the RiderPuck recenter FAB floats.
                                   // ScheduleSetup uses "top" so the growing address-entry card
                                   // (bottom-anchored) never covers the FAB.
}) {
  const [authFailed, setAuthFailed] = useState(false);
  const { theme } = useTheme();
  const styles = theme === "dark" ? MAP_STYLES_DARK : MAP_STYLES_LIGHT;

  // Google Maps invokes window.gm_authFailure when the JS API key is invalid
  // or the Maps JS API isn't enabled. Fall back to the static-map image so
  // the screen never goes blank.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const prev = window.gm_authFailure;
    window.gm_authFailure = () => setAuthFailed(true);
    return () => { window.gm_authFailure = prev; };
  }, []);

  if (!API_KEY || authFailed) {
    if (fallbackSrc) {
      return (
        <img
          key={fallbackSrc}
          src={fallbackSrc}
          alt="Route preview"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="route-map-fallback"
          onClick={onMapClick || undefined}
        />
      );
    }
    return (
      <div className="w-full h-full flex items-center justify-center bg-[#0A1128]/5 text-xs text-[#515E7B]">
        Map unavailable
      </div>
    );
  }

  return (
    <APIProvider apiKey={API_KEY} libraries={["geometry", "marker"]}>
      <Map
        defaultCenter={center}
        // Initial zoom is street-level (17 — buildings + storefronts
        // identifiable). The rider should immediately recognize their
        // immediate surroundings — landmarks visible, pickup area feels
        // personal and local, similar to Uber/Lyft's opening zoom. Once
        // a polyline arrives, RouteOverlays smoothly fitBounds with tight
        // padding so pickup + route + destination dominate the frame
        // rather than zooming back out to show large portions of the city.
        defaultZoom={17}
        gestureHandling="greedy"
        disableDefaultUI
        clickableIcons={false}
        styles={styles}
        mapId={process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || "DEMO_MAP_ID"}
        reuseMaps
        className="w-full h-full"
        onClick={onMapClick || undefined}
      >
        <RouteOverlays
          center={center}
          polyline={polyline}
          cinematicDraw={cinematicDraw}
          onDrawComplete={onDrawComplete}
          userLocation={userLocation}
          hideRecenter={hideRecenter}
          userHeading={userHeading}
          userMarkerVariant={userMarkerVariant}
          userMarkerState={userMarkerState}
        />
        {/* Shared Uber/Lyft-style puck. `RiderPuckBridge` uses vis.gl's
         *  useMap() hook to expose the underlying google.maps.Map to
         *  our headless <LocationPuck/>. Mounted alongside (and
         *  superseding) UserLocationDot for the new visual. */}
        <RiderPuckBridge recenterAnchor={recenterAnchor} />
      </Map>
    </APIProvider>
  );
}

/* =========================================================================
   Imperative overlays (polyline, markers, user dot, recenter button).
   We use legacy google.maps.Marker / Polyline / Circle so a Map ID isn't
   required in GCP. AdvancedMarker would force operators to configure one.
   ========================================================================= */
function RouteOverlays({ center, polyline, userLocation, hideRecenter, userHeading, userMarkerVariant, userMarkerState, cinematicDraw, onDrawComplete }) {
  const map = useMap();
  const geometryLib = useMapsLibrary("geometry");

  // Refs to a halo polyline + an array of gradient segment polylines so we can
  // tear them all down before drawing fresh state.
  const haloRef = useRef(null);
  const segmentsRef = useRef([]);
  const pickupMarkerRef = useRef(null);
  const destMarkerRef = useRef(null);

  // Defensive unmount cleanup — strip every polyline + endpoint marker
  // off the map when this component goes away, even if the parent caches
  // the underlying Map instance via `reuseMaps`. Without this, stepping
  // forward to /schedule plan + back has been observed to leave stale
  // polylines drawn on the map.
  useEffect(() => {
    return () => {
      if (haloRef.current) { haloRef.current.setMap(null); haloRef.current = null; }
      if (segmentsRef.current.length) {
        segmentsRef.current.forEach((p) => p.setMap(null));
        segmentsRef.current = [];
      }
      if (pickupMarkerRef.current) { pickupMarkerRef.current.setMap(null); pickupMarkerRef.current = null; }
      if (destMarkerRef.current) { destMarkerRef.current.setMap(null); destMarkerRef.current = null; }
    };
  }, []);

  useEffect(() => {
    if (!map || !geometryLib) return;
    // Tear-down stale overlays before any redraw.
    if (haloRef.current) { haloRef.current.setMap(null); haloRef.current = null; }
    if (segmentsRef.current.length) {
      segmentsRef.current.forEach((p) => p.setMap(null));
      segmentsRef.current = [];
    }
    if (pickupMarkerRef.current) { pickupMarkerRef.current.setMap(null); pickupMarkerRef.current = null; }
    if (destMarkerRef.current) { destMarkerRef.current.setMap(null); destMarkerRef.current = null; }

    if (!polyline) {
      if (center) map.panTo(center);
      return;
    }

    const path = geometryLib.encoding.decodePath(polyline);
    if (!path || path.length < 2) return;

    // Soft halo under the gradient — bumped from 7px to 10px so the
    // thicker route still reads clearly on the light cream map.
    haloRef.current = new window.google.maps.Polyline({
      path,
      strokeColor: "#0A1128",
      strokeOpacity: 0.14,
      strokeWeight: 10,
      zIndex: 1,
      map,
    });

    // Build N gradient segments interpolating #4B9CF8 → #ff7d22 along
    // the route — matches the "Where to?" entry title's gradient
    // exactly so the polyline reads as an extension of the same brand
    // paint. Oversample each polyline edge to keep the gradient smooth
    // on long highway stretches without rendering hundreds of overlays.
    const SEGMENTS = 30;
    const grad = buildGradient("#4B9CF8", "#ff7d22", SEGMENTS);
    const subdivided = subdivide(path, SEGMENTS);
    let cancelledDraw = false;
    let drawRaf = 0;

    if (cinematicDraw) {
      // Apple-Maps style: the whole polyline appears together and fades
      // in as the camera settles, rather than being progressively
      // "written" tip-by-tip. This eliminates the two competing RAF
      // loops (manual camera step + tip draw) that made the prior
      // 2.1s draw feel choppy — Google's native `fitBounds` handles
      // the camera cinematically on its own, and the polyline just
      // fades up smoothly beneath the pulse light that traces it.
      for (let i = 0; i < SEGMENTS; i++) {
        const seg = subdivided[i];
        if (!seg || seg.length < 2) continue;
        const line = new window.google.maps.Polyline({
          path: seg,
          strokeColor: grad[i],
          strokeOpacity: 0, // fade in from 0
          strokeWeight: 5,  // thicker per user request
          zIndex: 2,
          map,
        });
        segmentsRef.current.push(line);
      }
      const FADE_MS = 700;
      const t0 = performance.now();
      const easeOut = (t) => 1 - Math.pow(1 - t, 3);
      const step = () => {
        if (cancelledDraw) return;
        const linear = Math.min((performance.now() - t0) / FADE_MS, 1);
        const opacity = easeOut(linear) * 0.95;
        try {
          for (const seg of segmentsRef.current) seg.setOptions({ strokeOpacity: opacity });
          haloRef.current?.setOptions({ strokeOpacity: 0.14 * easeOut(linear) });
        } catch (_) { /* torn down */ }
        if (linear < 1) {
          drawRaf = requestAnimationFrame(step);
        } else {
          // Destination-pin pop the moment the fade completes — same
          // gesture Apple uses when a route arrives.
          try {
            const destMarker = destMarkerRef.current;
            if (destMarker) {
              const popStart = performance.now();
              const POP_MS = 320;
              const popIcon = (scale) => ({
                path: window.google.maps.SymbolPath.CIRCLE,
                scale,
                fillColor: "#ff7d22",
                fillOpacity: 1,
                strokeColor: "#FFFFFF",
                strokeWeight: 3,
              });
              const popStep = () => {
                if (cancelledDraw || !destMarkerRef.current) return;
                const t = Math.min((performance.now() - popStart) / POP_MS, 1);
                const s = t < 0.5 ? 1 + 0.7 * (t / 0.5) : 1.35 - 0.35 * ((t - 0.5) / 0.5);
                try { destMarker.setIcon(popIcon(8 * s)); } catch (_) { /* torn down */ }
                if (t < 1) requestAnimationFrame(popStep);
              };
              requestAnimationFrame(popStep);
            }
          } catch (_) { /* noop */ }
          if (typeof onDrawComplete === "function") {
            try { onDrawComplete(); } catch (_) { /* consumer bug — do not crash */ }
          }
        }
      };
      drawRaf = requestAnimationFrame(step);
    } else {
      // Static (non-cinematic) draw — render every segment immediately.
      for (let i = 0; i < SEGMENTS; i++) {
        const seg = subdivided[i];
        if (!seg || seg.length < 2) continue;
        const line = new window.google.maps.Polyline({
          path: seg,
          strokeColor: grad[i],
          strokeOpacity: 0.95,
          strokeWeight: 5,
          zIndex: 2,
          map,
        });
        segmentsRef.current.push(line);
      }
      // Hand-off — same contract as the cinematic path, deferred a frame
      // so the caller can react on the tick AFTER the polylines mount.
      if (typeof onDrawComplete === "function") {
        try { requestAnimationFrame(() => onDrawComplete()); } catch (_) { /* noop */ }
      }
    }

    // Endpoint markers (pickup blue, destination orange).
    const dot = (color) => ({
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: 8,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: "#FFFFFF",
      strokeWeight: 3,
    });
    pickupMarkerRef.current = new window.google.maps.Marker({
      position: path[0], map, icon: dot("#4B9CF8"), title: "Pickup", zIndex: 10,
    });
    destMarkerRef.current = new window.google.maps.Marker({
      position: path[path.length - 1], map, icon: dot("#ff7d22"), title: "Destination", zIndex: 11,
    });

    const bounds = new window.google.maps.LatLngBounds();
    path.forEach((p) => bounds.extend(p));
    // Native fitBounds — Google's own camera easing is smoother than
    // any manual RAF-per-frame setZoom/setCenter loop. The previous
    // manual camera path fought the RAF polyline draw and produced the
    // choppy zoom the user reported. Now the camera moves on Google's
    // internal timeline and the polyline just fades in over it.
    const padding = cinematicDraw
      ? { top: 140, right: 80, bottom: 260, left: 80 }
      : { top: 96, right: 40, bottom: 200, left: 40 };
    try { map.fitBounds(bounds, padding); } catch (_) { /* torn down */ }
    return () => {
      cancelledDraw = true;
      if (drawRaf) cancelAnimationFrame(drawRaf);
      if (pulseRafRef.current) cancelAnimationFrame(pulseRafRef.current);
      if (pulseMarkerRef.current) { pulseMarkerRef.current.setMap(null); pulseMarkerRef.current = null; }
    };
  }, [map, geometryLib, polyline, center, cinematicDraw]);

  // Round-trip pulse — after the polyline finishes drawing (cinematic
  // mode only), a soft light comet travels forward along the SAME path
  // to the destination, then retraces BACKWARD to the pickup, forever.
  // The turnaround is masked by motion (not geometry) — do NOT change
  // this to an offset arc, Link rides use the same route both ways:
  //   1. HOLD ~250ms at each endpoint (arrival beat).
  //   2. FADE the comet's opacity through the reversal — never visible
  //      moving backward "while bright".
  //   3. FLARE the endpoint pin (scale up 1.3x, then back) when the
  //      comet arrives, drawing the eye to the pin during reversal.
  // Reduced-motion: pulse never starts (cinematicDraw is false when
  // reduce is set, and we gate on it below).
  const pulseMarkerRef = useRef(null);
  const pulseRafRef = useRef(0);
  useEffect(() => {
    // Wait for the cinematic draw to finish before starting the pulse.
    // No pulse if the path is missing or animation is disabled.
    if (!map || !geometryLib || !polyline || !cinematicDraw) return;
    const path = geometryLib.encoding.decodePath(polyline);
    if (!path || path.length < 2) return;

    // Kill any previous pulse first — parent-driven prop changes could
    // otherwise leave stale comets on the map.
    if (pulseRafRef.current) cancelAnimationFrame(pulseRafRef.current);
    if (pulseMarkerRef.current) { pulseMarkerRef.current.setMap(null); pulseMarkerRef.current = null; }

    // Build a cumulative-distance table so we can interpolate at
    // constant screen speed along an irregular polyline.
    const total = geometryLib.spherical.computeLength(path);
    if (!total || total < 10) return;
    const cum = [0];
    for (let i = 1; i < path.length; i++) {
      cum.push(cum[i - 1] + geometryLib.spherical.computeDistanceBetween(path[i - 1], path[i]));
    }
    const posAt = (frac) => {
      const dist = frac * total;
      let lo = 0, hi = cum.length - 1;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (cum[mid] < dist) lo = mid + 1; else hi = mid;
      }
      const idx = Math.max(1, lo);
      const segLen = cum[idx] - cum[idx - 1];
      const segFrac = segLen === 0 ? 0 : (dist - cum[idx - 1]) / segLen;
      return geometryLib.spherical.interpolate(path[idx - 1], path[idx], segFrac);
    };

    const pulseIcon = (opacity = 1) => ({
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: 7,
      fillColor: "#ED6603",
      fillOpacity: opacity,
      strokeColor: "#FFFFFF",
      strokeOpacity: opacity * 0.9,
      strokeWeight: 2,
    });

    pulseMarkerRef.current = new window.google.maps.Marker({
      position: path[0],
      map,
      icon: pulseIcon(0),
      zIndex: 20,
      clickable: false,
      optimized: true,
    });

    // Timing per lap:
    //   0     → 2400ms   forward (pickup → destination), fast-out slow-in
    //   2400  → 2650ms   hold at destination (dim to 0, flare pin)
    //   2650  → 5050ms   backward retrace (destination → pickup)
    //   5050  → 5300ms   hold at pickup (dim to 0, flare pin)
    // Loop.
    const LEG_MS = 2400;
    const HOLD_MS = 250;
    const LOOP_MS = (LEG_MS + HOLD_MS) * 2;
    const easeFastOutSlowIn = (t) => 1 - Math.pow(1 - t, 3); // strong ease-out per leg
    const t0 = performance.now();
    let pinFlareStart = 0;
    let lastFlareEnd = 0; // "pickup" | "dest" so we only flare once per arrival

    const step = () => {
      const now = performance.now();
      const local = (now - t0) % LOOP_MS;
      let frac = 0;
      let opacity = 1;
      let arrivedEnd = null; // "dest" | "pickup" | null
      if (local < LEG_MS) {
        // Forward leg
        const t = easeFastOutSlowIn(local / LEG_MS);
        frac = t;
        // Fade IN over the first 200ms of forward leg
        opacity = Math.min(1, local / 200);
      } else if (local < LEG_MS + HOLD_MS) {
        // Hold at destination — dim + flare pin
        frac = 1;
        const holdT = (local - LEG_MS) / HOLD_MS;
        opacity = 1 - holdT;
        arrivedEnd = "dest";
      } else if (local < LEG_MS * 2 + HOLD_MS) {
        // Backward leg — retrace SAME path
        const t = easeFastOutSlowIn((local - LEG_MS - HOLD_MS) / LEG_MS);
        frac = 1 - t;
        // Fade IN during first 200ms of return leg
        opacity = Math.min(1, (local - LEG_MS - HOLD_MS) / 200);
      } else {
        // Hold at pickup — dim + flare pin
        frac = 0;
        const holdT = (local - LEG_MS * 2 - HOLD_MS) / HOLD_MS;
        opacity = 1 - holdT;
        arrivedEnd = "pickup";
      }

      try {
        pulseMarkerRef.current?.setPosition(posAt(frac));
        pulseMarkerRef.current?.setIcon(pulseIcon(opacity));
      } catch (_) { /* torn down mid-frame */ }

      // Pin flare — trigger exactly once per arrival, animate scale over 250ms
      if (arrivedEnd && arrivedEnd !== lastFlareEnd) {
        lastFlareEnd = arrivedEnd;
        pinFlareStart = now;
        const target = arrivedEnd === "dest" ? destMarkerRef.current : pickupMarkerRef.current;
        const baseColor = arrivedEnd === "dest" ? "#ed6500" : "#0074d4";
        const flareStep = () => {
          const elapsed = performance.now() - pinFlareStart;
          if (elapsed >= 250) {
            try { target?.setIcon({ path: window.google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: baseColor, fillOpacity: 1, strokeColor: "#FFFFFF", strokeWeight: 3 }); } catch (_) { /* torn down */ }
            return;
          }
          const t = elapsed / 250;
          const scale = 8 + 4 * (1 - Math.pow(1 - t, 2)) * (t < 0.5 ? 1 : (1 - t) * 2);
          try {
            target?.setIcon({ path: window.google.maps.SymbolPath.CIRCLE, scale, fillColor: baseColor, fillOpacity: 1, strokeColor: "#FFFFFF", strokeWeight: 3 });
          } catch (_) { /* torn down */ }
          requestAnimationFrame(flareStep);
        };
        requestAnimationFrame(flareStep);
      } else if (!arrivedEnd) {
        // reset lastFlareEnd once we've left both endpoints so the next
        // arrival flares cleanly
        if (frac > 0.02 && frac < 0.98) lastFlareEnd = 0;
      }

      pulseRafRef.current = requestAnimationFrame(step);
    };
    // Delay pulse start until AFTER the polyline fade-in completes
    // (~700ms). Then the traveling light picks up where the fade
    // ends — the whole beat feels like Apple Maps: line appears →
    // light immediately traces it, on loop.
    const startTimer = setTimeout(() => { pulseRafRef.current = requestAnimationFrame(step); }, 750);
    return () => {
      clearTimeout(startTimer);
      if (pulseRafRef.current) cancelAnimationFrame(pulseRafRef.current);
      if (pulseMarkerRef.current) { pulseMarkerRef.current.setMap(null); pulseMarkerRef.current = null; }
    };
  }, [map, geometryLib, polyline, cinematicDraw]);

  // ---------------------------------------------------------------------
  // Cinematic camera state machine (iter 67)
  //
  // STATE A  Idle / pre-route
  //   • First geolocation resolve → cinematic "settle" pan + zoom-in from
  //     the wider default to a neighborhood (z=15) target. Animated, not
  //     instant. Single-shot per session.
  //
  // STATE B  Route preview
  //   • Handled above: fitBounds with edge padding when a polyline arrives.
  //     Google Maps animates fitBounds smoothly out-of-the-box.
  //
  // STATE C  Ride active (future)
  //   • follow-mode hooks live here too once driver tracking ships; we
  //     leave the structure (didInitialPanRef + userMovedRef) compatible.
  //
  // User-gesture respect
  //   • We hook 'dragstart'/'zoom_changed' triggered by user input. Once
  //     the rider has manually nudged the map, we never re-center
  //     ourselves — fixes the "camera fights me" feel.
  // ---------------------------------------------------------------------
  const didInitialPanRef = useRef(false);
  const userMovedRef = useRef(false);

  // Subscribe once: any user-driven drag flips userMovedRef so future
  // initial-pan attempts no-op.
  useEffect(() => {
    if (!map) return;
    const onDragStart = () => { userMovedRef.current = true; };
    const listener = map.addListener("dragstart", onDragStart);
    return () => { try { listener?.remove(); } catch (_) { /* noop */ } };
  }, [map]);

  useEffect(() => {
    if (!map || polyline || !center) return;
    if (didInitialPanRef.current || userMovedRef.current) return;
    didInitialPanRef.current = true;

    // Cinematic settle: a brief delay so the map tile-set has time to
    // populate, then panTo + a graceful step toward street zoom. We
    // choose 16 (street-level — pickup pin pops, individual buildings
    // resolve) so the rider's immediate surroundings dominate the screen
    // the way they do in Uber/Lyft. The 250ms delay lets the initial
    // defaultZoom render before we visibly zoom in.
    const t = setTimeout(() => {
      try {
        if (userMovedRef.current) return; // gestural override
        map.panTo(center);
        const target = 17;
        const current = map.getZoom();
        if (current == null || Math.abs(current - target) >= 1) {
          // Google Maps animates setZoom over ~300ms when called from
          // user code — we lean on that built-in easing rather than
          // shipping our own RAF interpolator (which would fight the
          // tile loader and look jankier).
          map.setZoom(target);
        }
      } catch (_) { /* defensive */ }
    }, 250);
    return () => clearTimeout(t);
  }, [map, center, polyline]);

  return (
    <>
      {/* Legacy UserLocationDot REMOVED — superseded by <RiderPuckBridge/>
       *  which mounts the shared Uber-style LocationPuck AND owns the
       *  recenter / locate-me toggle (puck mode + motion-permission
       *  request). The standalone RecenterButton has moved inside the
       *  bridge so the toggle works against the live puck state. */}
    </>
  );
}

/* ---------- gradient helpers (kept module-private; tiny + dependency-free) ----- */
function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
function rgbToHex(r, g, b) {
  const t = (n) => n.toString(16).padStart(2, "0");
  return `#${t(r)}${t(g)}${t(b)}`;
}
function lerp(a, b, t) { return Math.round(a + (b - a) * t); }
function buildGradient(start, end, n) {
  const a = hexToRgb(start), b = hexToRgb(end);
  const out = [];
  for (let i = 0; i < n; i++) {
    const t = i / Math.max(1, n - 1);
    out.push(rgbToHex(lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)));
  }
  return out;
}
/**
 * Split a polyline path of variable-length edges into N approximately-equal
 * sub-paths. Each output element is a path array suitable for a Polyline.
 * Adjacent segments share a vertex so the line reads as one continuous stroke.
 */
function subdivide(path, n) {
  // Cumulative arc length using straight-line distance (ENU is fine for visual
  // gradient — we don't need geodesic precision).
  const cum = [0];
  for (let i = 1; i < path.length; i++) {
    const dx = path[i].lng() - path[i - 1].lng();
    const dy = path[i].lat() - path[i - 1].lat();
    cum.push(cum[i - 1] + Math.sqrt(dx * dx + dy * dy));
  }
  const total = cum[cum.length - 1];
  if (total === 0) return [];
  const out = [];
  let segIdx = 0;
  let lastPoint = path[0];
  for (let s = 1; s <= n; s++) {
    const target = (total * s) / n;
    const seg = [lastPoint];
    while (segIdx < cum.length - 1 && cum[segIdx + 1] < target) {
      segIdx++;
      seg.push(path[segIdx]);
    }
    if (segIdx < cum.length - 1) {
      // interpolate the boundary point
      const t = (target - cum[segIdx]) / (cum[segIdx + 1] - cum[segIdx]);
      const lat = path[segIdx].lat() + (path[segIdx + 1].lat() - path[segIdx].lat()) * t;
      const lng = path[segIdx].lng() + (path[segIdx + 1].lng() - path[segIdx].lng()) * t;
      const p = new window.google.maps.LatLng(lat, lng);
      seg.push(p);
      lastPoint = p;
    } else {
      seg.push(path[path.length - 1]);
      lastPoint = path[path.length - 1];
    }
    out.push(seg);
  }
  return out;
}

/* ---------- User-location pulsing dot (rider blue) + branded driver marker ----------
 * variant="default" → blue dot (rider/booking flow)
 * variant="driver"  → branded orange marker with rotating heading arrow,
 *                      smooth position+heading interpolation, state-driven shade.
 * state (driver only):
 *   "idle"     → dimmed orange (no active leg)
 *   "en_route" → bright orange (driver_en_route / arriving_soon)
 *   "on_trip"  → bright orange + brighter glow (in_progress)
 */
const DRIVER_ORANGE        = "#ed6500";
const DRIVER_ORANGE_DIM    = "#a04600";
const DRIVER_ARROW_FILL    = "#FFFFFF";
const ANIM_DURATION_MS     = 450;
const PULSE_PERIOD_MS      = 1800;

const STATE_STYLE = {
  idle:     { body: DRIVER_ORANGE_DIM, ringOpacity: 0.18, ringMax: 140 },
  en_route: { body: DRIVER_ORANGE,     ringOpacity: 0.22, ringMax: 160 },
  on_trip:  { body: DRIVER_ORANGE,     ringOpacity: 0.32, ringMax: 200 },
};

function UserLocationDot({ map, position, heading = null, variant = "default", state = "en_route" }) {
  const bodyMarkerRef  = useRef(null);
  const arrowMarkerRef = useRef(null);
  const ringRef        = useRef(null);
  const pulseRef       = useRef(null);
  const animRef        = useRef({ raf: null, t0: 0,
                                  startLat: 0, startLng: 0, endLat: 0, endLng: 0,
                                  startHeading: 0, endHeading: 0 });

  const isDriver = variant === "driver";
  const headingValid = isDriver && Number.isFinite(heading);
  const style = STATE_STYLE[state] || STATE_STYLE.en_route;
  const bodyColor = isDriver ? style.body : USER_BLUE;
  const ringColor = bodyColor;

  useEffect(() => {
    if (!map) return;
    if (!position) {
      if (bodyMarkerRef.current)  { bodyMarkerRef.current.setMap(null);  bodyMarkerRef.current  = null; }
      if (arrowMarkerRef.current) { arrowMarkerRef.current.setMap(null); arrowMarkerRef.current = null; }
      if (ringRef.current)        { ringRef.current.setMap(null);        ringRef.current        = null; }
      if (pulseRef.current)       { clearInterval(pulseRef.current);     pulseRef.current       = null; }
      if (animRef.current.raf)    { cancelAnimationFrame(animRef.current.raf); animRef.current.raf = null; }
      return;
    }

    // ---- Body marker (orange filled circle, slightly larger for driver) ----
    const bodyIcon = {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: isDriver ? 9 : 7,
      fillColor: bodyColor,
      fillOpacity: 1,
      strokeColor: "#FFFFFF",
      strokeWeight: 3,
    };
    if (!bodyMarkerRef.current) {
      bodyMarkerRef.current = new window.google.maps.Marker({
        position, map, zIndex: 50, icon: bodyIcon,
        title: isDriver ? "Driver" : "Your location",
        clickable: false,
      });
    } else {
      bodyMarkerRef.current.setIcon(bodyIcon);
    }

    // ---- Arrow marker (driver-only, only when heading is known) ----
    if (headingValid) {
      const arrowIcon = {
        // Triangle pointing up at rotation=0; sits above body so it reads
        // as a navigation chevron on top of the dot.
        path: "M 0,-20 L 6,-10 L -6,-10 Z",
        fillColor: DRIVER_ARROW_FILL,
        fillOpacity: 1,
        strokeColor: bodyColor,
        strokeWeight: 1.5,
        rotation: heading,
        anchor: new window.google.maps.Point(0, 0),
        scale: 1,
      };
      if (!arrowMarkerRef.current) {
        arrowMarkerRef.current = new window.google.maps.Marker({
          position, map, zIndex: 51, icon: arrowIcon, clickable: false,
        });
      } else {
        arrowMarkerRef.current.setIcon(arrowIcon);
      }
    } else if (arrowMarkerRef.current) {
      // Heading lost (stationary) — hide the arrow rather than freeze a stale one.
      arrowMarkerRef.current.setMap(null);
      arrowMarkerRef.current = null;
    }

    // ---- Ring (pulsing halo) ----
    if (!ringRef.current) {
      ringRef.current = new window.google.maps.Circle({
        center: position, radius: 80, map,
        strokeOpacity: 0,
        fillColor: ringColor, fillOpacity: style.ringOpacity,
        clickable: false, zIndex: 49,
      });
    } else {
      ringRef.current.setOptions({ fillColor: ringColor });
    }

    // ---- Smooth position + heading interpolation (~450ms ease-out) ----
    const prevLat = animRef.current.endLat || position.lat;
    const prevLng = animRef.current.endLng || position.lng;
    const prevHeading = animRef.current.endHeading || (headingValid ? heading : 0);

    // Shortest-path heading delta (handles 359→1 wrap)
    let nextHeading = prevHeading;
    if (headingValid) {
      let delta = heading - prevHeading;
      while (delta > 180)  delta -= 360;
      while (delta < -180) delta += 360;
      nextHeading = prevHeading + delta;
    }

    if (animRef.current.raf) cancelAnimationFrame(animRef.current.raf);
    animRef.current.startLat = prevLat;
    animRef.current.startLng = prevLng;
    animRef.current.endLat = position.lat;
    animRef.current.endLng = position.lng;
    animRef.current.startHeading = prevHeading;
    animRef.current.endHeading = nextHeading;
    animRef.current.t0 = performance.now();

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    const tick = () => {
      const t = Math.min(1, (performance.now() - animRef.current.t0) / ANIM_DURATION_MS);
      const e = easeOutCubic(t);
      const lat = animRef.current.startLat + (animRef.current.endLat - animRef.current.startLat) * e;
      const lng = animRef.current.startLng + (animRef.current.endLng - animRef.current.startLng) * e;
      const hd  = animRef.current.startHeading + (animRef.current.endHeading - animRef.current.startHeading) * e;
      const ll = new window.google.maps.LatLng(lat, lng);
      if (bodyMarkerRef.current) bodyMarkerRef.current.setPosition(ll);
      if (ringRef.current)       ringRef.current.setCenter(ll);
      if (arrowMarkerRef.current) {
        arrowMarkerRef.current.setPosition(ll);
        const cur = arrowMarkerRef.current.getIcon();
        arrowMarkerRef.current.setIcon({ ...cur, rotation: hd });
      }
      if (t < 1) {
        animRef.current.raf = requestAnimationFrame(tick);
      } else {
        animRef.current.raf = null;
      }
    };
    animRef.current.raf = requestAnimationFrame(tick);

    // ---- Halo pulse (radius + opacity, scaled by state) ----
    if (pulseRef.current) clearInterval(pulseRef.current);
    const start = performance.now();
    pulseRef.current = setInterval(() => {
      if (!ringRef.current) return;
      const t = ((performance.now() - start) % PULSE_PERIOD_MS) / PULSE_PERIOD_MS;
      ringRef.current.setRadius(80 + t * style.ringMax);
      ringRef.current.setOptions({ fillOpacity: style.ringOpacity * (1 - t) });
    }, 33);

    return () => {
      if (pulseRef.current) { clearInterval(pulseRef.current); pulseRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, position?.lat, position?.lng, heading, isDriver, state]);

  // Final unmount cleanup
  useEffect(() => () => {
    if (bodyMarkerRef.current)  { bodyMarkerRef.current.setMap(null);  bodyMarkerRef.current  = null; }
    if (arrowMarkerRef.current) { arrowMarkerRef.current.setMap(null); arrowMarkerRef.current = null; }
    if (ringRef.current)        { ringRef.current.setMap(null);        ringRef.current        = null; }
    if (pulseRef.current)       { clearInterval(pulseRef.current);     pulseRef.current       = null; }
    if (animRef.current.raf)    { cancelAnimationFrame(animRef.current.raf); animRef.current.raf = null; }
  }, []);

  return null;
}

/* ---------- Floating Recenter / Locate-me button ----------
 * When `onLocate` is provided, tapping the button delegates to it
 * (handles permission flow + follow-mode toggle on the rider map).
 * Otherwise we fall back to a simple panTo on `target`. */
function RecenterButton({ map, target, hidden = false, onLocate, active = false, anchor = "bottom" }) {
  if (hidden) return null;
  const handleClick = () => {
    if (onLocate) { onLocate(); return; }
    if (!target || !map) return;
    map.panTo(target);
    const z = map.getZoom();
    if (z == null || z < 13) map.setZoom(14);
  };
  // Anchor variants:
  //   • "bottom" — legacy position, floats bottom-right above safe-area.
  //   • "top"    — floats just below the top account pill (safe + 12 + 76
  //     + 20 = safe + 108). Used on the rider ScheduleSetup map so the
  //     growing address-entry card (bottom-anchored) never covers it.
  const positionStyle = anchor === "top"
    ? { top: "calc(env(safe-area-inset-top, 0px) + 108px)" }
    : { bottom: "calc(env(safe-area-inset-bottom, 0px) + 22px)" };
  return (
    <button
      type="button"
      onClick={handleClick}
      style={positionStyle}
      className={`fixed right-3 z-40 w-11 h-11 rounded-full shadow-lg border hairline flex items-center justify-center active:scale-95 transition-colors ${active ? "bg-[#0A1128] text-[#F9F7F4]" : "bg-white text-[#0A1128]"}`}
      aria-label="Recenter map on my location"
      aria-pressed={active}
      data-testid="map-recenter-btn"
    >
      <LocateFixed size={18} strokeWidth={1.8} />
    </button>
  );
}


/**
 * RiderPuckBridge — vis.gl ⇄ shared LocationPuck adapter.
 *
 * vis.gl/react-google-maps owns the Map instance; we use its `useMap()`
 * hook to hand the underlying google.maps.Map object to our shared
 * headless `<LocationPuck/>`. The booking surface stays north-up
 * (mode="static") — the rider doesn't need a heading-following camera
 * while picking pickup/dropoff. The puck only renders the smoothed
 * dot+cone so the rider sees their position with the same fidelity
 * as the driver app.
 */
function RiderPuckBridge({ recenterAnchor = "bottom" }) {
  const map = useMap();
  const puck = useUserPuck({ active: !!map });
  const [puckMode, setPuckMode] = useState("static");

  // The recenter / locate-me toggle. Inside the gesture stack:
  // 1) Asks for DeviceOrientation permission (iOS Safari needs gesture).
  // 2) Asks for geolocation if not granted.
  // 3) Flips into follow mode so the puck cone + camera follow start.
  // Tap again while already following → navigation mode (camera rotates
  // with heading + tilt). Third tap → back to static.
  const onLocate = useCallback(async () => {
    if (puckMode === "static") {
      try { await puck.request(); } catch { /* ignore */ }
      setPuckMode("follow");
    } else if (puckMode === "follow") {
      setPuckMode("navigation");
    } else {
      setPuckMode("static");
    }
    if (puck.lat != null && map) {
      map.panTo({ lat: puck.lat, lng: puck.lng });
      if (typeof map.getZoom === "function" && map.getZoom() < 16) map.setZoom(16);
    }
  }, [puck, puckMode, map]);

  return (
    <>
      <LocationPuck
        map={map}
        lat={puck.lat}
        lng={puck.lng}
        heading={puck.heading}
        headingConfident={puck.headingConfident}
        mode={puckMode}
        followZoom={17}
        onUserGesture={() => setPuckMode("static")}
      />
      <RecenterButton
        map={map}
        target={puck.lat != null ? { lat: puck.lat, lng: puck.lng } : null}
        onLocate={onLocate}
        active={puckMode !== "static"}
        anchor={recenterAnchor}
      />
    </>
  );
}
